import React, { useEffect, useState, useMemo } from 'react';
import API from '../api';
import { toast } from 'react-toastify';
import { usePharmacy } from '../context/PharmacyContext';

export default function DamageManagementPage() {
  const [damages, setDamages] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('Pending');
  const [q, setQ] = useState('');

  // Modals
  const [showAddForm, setShowAddForm] = useState(false);
  const [approveModal, setApproveModal] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [selectedMed, setSelectedMed] = useState('');
  
  // Forms
  const [formData, setFormData] = useState({
    medicineId: '', batchId: '', damageType: 'Broken/Damaged', quantity: '', notes: ''
  });
  const [rejectReason, setRejectReason] = useState('');
  const { pharmacyDetails } = usePharmacy();

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dmgRes, medRes] = await Promise.all([
        API.get('/damages'),
        API.get('/medicines')
      ]);
      setDamages(dmgRes.data);
      setMedicines(medRes.data.filter(m => m.quantity > 0));
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Fetch batches when medicine is selected
  useEffect(() => {
    if (formData.medicineId) {
      API.get(`/medicines/${formData.medicineId}/batches?active=true`)
        .then(res => setBatches(res.data))
        .catch(() => toast.error('Failed to load batches'));
    } else {
      setBatches([]);
    }
  }, [formData.medicineId]);

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.post('/damages', formData);
      toast.success('Damage reported successfully. Stock deducted pending approval.');
      setShowAddForm(false);
      setFormData({ medicineId: '', batchId: '', damageType: 'Broken/Damaged', quantity: '', notes: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error reporting damage');
    }
  };

  const generateAuditReport = async (dmg) => {
    const today = new Date();
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Inventory Write-Off Report - ${dmg.damageInvoiceNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Arial', sans-serif; padding: 20px; color: #1a202c; font-size: 13px; line-height: 1.4; }
          .header { text-align: center; border-bottom: 2px solid #dc2626; padding-bottom: 15px; margin-bottom: 20px; }
          .header h1 { color: #dc2626; font-size: 22px; text-transform: uppercase; margin-bottom: 5px; }
          .header p { color: #64748b; font-size: 12px; }
          .section { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px; margin-bottom: 20px; }
          .section h2 { color: #1e293b; font-size: 16px; margin-bottom: 10px; border-bottom: 1px solid #cbd5e1; padding-bottom: 5px; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
          .item { margin-bottom: 8px; }
          .item span { color: #64748b; font-weight: bold; display: inline-block; width: 120px; }
          .financial { background: #fef2f2; border: 1px solid #fecaca; }
          .financial h2 { color: #991b1b; border-color: #fecaca; }
          .total { font-size: 18px; font-weight: bold; color: #dc2626; margin-top: 10px; padding-top: 10px; border-top: 2px solid #fecaca; }
          .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 40px; }
          .sig-box { text-align: center; border-top: 1px solid #1e293b; padding-top: 5px; color: #64748b; }
          @media print { body { padding: 10px; } }
          @page { size: A4; margin: 0.5in; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${pharmacyDetails.pharmacyName || pharmacyDetails.name || 'Pharmacy'}</h1>
          <p>OFFICIAL INVENTORY WRITE-OFF & DISPOSAL AUDIT RECORD</p>
        </div>
        
        <div class="section">
          <h2>Document Details</h2>
          <div class="grid">
            <div class="item"><span>Reference No:</span> <strong>${dmg.damageInvoiceNumber}</strong></div>
            <div class="item"><span>Report Date:</span> ${new Date(dmg.dateReported).toLocaleDateString('en-IN')}</div>
            <div class="item"><span>Status:</span> <strong style="color:#16a34a">APPROVED & WRITTEN OFF</strong></div>
            <div class="item"><span>Damage Category:</span> ${dmg.damageType}</div>
          </div>
        </div>

        <div class="section">
          <h2>Item Details</h2>
          <div class="grid">
            <div class="item"><span>Medicine Name:</span> <strong>${dmg.medicine?.name}</strong></div>
            <div class="item"><span>Brand:</span> ${dmg.medicine?.brand || 'N/A'}</div>
            <div class="item"><span>Batch No:</span> ${dmg.batch?.batchNumber || 'N/A'}</div>
            <div class="item"><span>Expiry Date:</span> ${dmg.batch?.expiryDate ? new Date(dmg.batch.expiryDate).toLocaleDateString('en-IN') : 'N/A'}</div>
          </div>
          <div class="item" style="margin-top: 10px;"><span>Reason/Notes:</span> ${dmg.notes || 'No notes provided'}</div>
        </div>

        <div class="section financial">
          <h2>Financial Impact Assessment</h2>
          <div class="item"><span>Disposed Quantity:</span> ${dmg.quantity} ${dmg.medicine?.quantityUnit || 'units'}</div>
          <div class="item"><span>Unit Cost Price:</span> Rs ${Number(dmg.unitCostPrice).toFixed(2)}</div>
          <div class="total"><span>TOTAL NET LOSS:</span> Rs ${Number(dmg.totalLossValue).toFixed(2)}</div>
        </div>

        <div style="font-size: 11px; color: #64748b; text-align: justify; margin-bottom: 20px;">
          <strong>DECLARATION:</strong> This document certifies that the above-mentioned inventory has been identified as unfit for sale or use due to the stated reason. The stock has been permanently deducted from the active inventory management system, and the corresponding financial loss has been recorded in the business ledger. The physical items shall be disposed of in accordance with applicable pharmacy regulatory and environmental compliance guidelines.
        </div>

        <div class="signatures">
          <div class="sig-box">
            <strong>REPORTED BY</strong><br>
            ${dmg.reportedBy?.name || 'Staff Member'}
          </div>
          <div class="sig-box">
            <strong>AUTHORIZED & APPROVED BY</strong><br>
            Pharmacy Manager / Admin
          </div>
        </div>
      </body>
      </html>
    `;
    
    try {
      await API.put(`/damages/${dmg._id}/approve`, { reportHtml: html });
      toast.success('Damage Approved and Financial Loss Recorded.');
      setApproveModal(null);
      fetchData();

      // Open print window
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const printWin = window.open(url, '_blank');
      
      printWin.addEventListener('load', () => {
        setTimeout(() => printWin.print(), 500);
        const check = setInterval(() => {
          if (printWin.closed) {
            clearInterval(check);
            URL.revokeObjectURL(url);
            window.focus();
          }
        }, 500);
      });
    } catch (err) {
      toast.error('Error approving damage');
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    try {
      await API.put(`/damages/${rejectModal._id}/reject`, { rejectionReason: rejectReason });
      toast.success('Damage rejected. Stock reverted to inventory.');
      setRejectModal(null);
      setRejectReason('');
      fetchData();
    } catch (err) {
      toast.error('Error rejecting damage');
    }
  };

  const handleViewReport = async (id) => {
    try {
      const { data } = await API.get(`/damages/${id}/report`);
      const blob = new Blob([data.reportHtml], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const printWin = window.open(url, '_blank');
      
      const check = setInterval(() => {
        if (printWin.closed) {
          clearInterval(check);
          URL.revokeObjectURL(url);
          window.focus();
        }
      }, 500);
    } catch (err) {
      toast.error('Report document not found');
    }
  };

  const filteredDamages = damages.filter(d => {
    const matchStatus = filter === 'ALL' || d.status === filter;
    const matchSearch = q === '' || d.medicine?.name.toLowerCase().includes(q.toLowerCase()) || d.damageInvoiceNumber.toLowerCase().includes(q.toLowerCase());
    return matchStatus && matchSearch;
  });

  const stats = {
    totalValue: damages.filter(d => d.status === 'Approved').reduce((s, d) => s + d.totalLossValue, 0),
    pendingItems: damages.filter(d => d.status === 'Pending').length,
    approvedItems: damages.filter(d => d.status === 'Approved').length
  };

  const fmt = (num) => Number(num || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Styles Matching the Blue Theme
  const styles = {
    container: { minHeight: '100vh', background: '#f5f7fa', fontFamily: "'Inter', sans-serif" },
    header: { background: '#ffffff', borderBottom: '1px solid #e1e8ed', padding: isMobile ? '16px' : '20px 32px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 12 : 0, position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
    headerTitle: { display: 'flex', alignItems: 'center', gap: 12 },
    headerIcon: { width: 40, height: 40, background: '#1880c2', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 14, fontWeight: 700 },
    headerText: { fontSize: 20, fontWeight: 700, color: '#1a202c' },
    headerSubtext: { fontSize: 12, color: '#718096', fontWeight: 500 },
    main: { maxWidth: 1400, margin: '0 auto', padding: isMobile ? '16px' : '24px 32px' },
    statsRow: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16, marginBottom: 24 },
    statCard: { background: '#ffffff', borderRadius: 10, padding: 20, border: '1px solid #e1e8ed', display: 'flex', gap: 12, alignItems: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
    statIconBox: { width: 48, height: 48, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700 },
    statNumber: { fontSize: 22, fontWeight: 800, color: '#1a202c' },
    statTitle: { fontSize: 11, fontWeight: 600, color: '#718096', textTransform: 'uppercase' },
    card: { background: '#ffffff', borderRadius: 10, border: '1px solid #e1e8ed', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
    cardHeader: { padding: '16px 24px', borderBottom: '1px solid #e1e8ed', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafbfc', flexWrap: 'wrap', gap: 12 },
    btnPrimary: { padding: '10px 20px', background: '#1880c2', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 14 },
    btnSecondary: { padding: '10px 20px', background: '#ffffff', color: '#4a5568', border: '1px solid #e1e8ed', borderRadius: 6, fontWeight: 600, cursor: 'pointer' },
    btnDanger: { padding: '10px 20px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, fontWeight: 600, cursor: 'pointer' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#718096', background: '#fafbfc', borderBottom: '1px solid #e1e8ed', textTransform: 'uppercase' },
    td: { padding: '14px 16px', fontSize: 14, color: '#1a202c', borderBottom: '1px solid #f7fafc' },
    badge: { padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700 },
    modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: 16 },
    modalContent: { background: '#ffffff', borderRadius: 16, width: isMobile ? '100%' : '500px', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' },
    input: { padding: '10px 14px', borderRadius: 6, border: '1px solid #e1e8ed', fontSize: 14, width: '100%', boxSizing: 'border-box', outline: 'none' }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerTitle}>
          <div style={styles.headerIcon}>DMG</div>
          <div>
            <div style={styles.headerText}>Damage & Loss Management</div>
            <div style={styles.headerSubtext}>Track non-returnable inventory write-offs</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowAddForm(true)} style={styles.btnPrimary}>
            + Report Damage
          </button>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIconBox, background: '#fef2f2', color: '#dc2626' }}>LOSS</div>
            <div>
              <div style={{ ...styles.statNumber, color: '#dc2626' }}>Rs {fmt(stats.totalValue)}</div>
              <div style={styles.statTitle}>Total Financial Loss</div>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIconBox, background: '#fffbeb', color: '#d97706' }}>PEN</div>
            <div>
              <div style={styles.statNumber}>{stats.pendingItems}</div>
              <div style={styles.statTitle}>Pending Approvals</div>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIconBox, background: '#f0fdf4', color: '#16a34a' }}>APR</div>
            <div>
              <div style={styles.statNumber}>{stats.approvedItems}</div>
              <div style={styles.statTitle}>Approved Write-offs</div>
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {['Pending', 'Approved', 'Rejected', 'ALL'].map(tab => (
                <button 
                  key={tab} 
                  onClick={() => setFilter(tab)} 
                  style={{
                    padding: '8px 16px', borderRadius: 20, border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: 13,
                    background: filter === tab ? '#1880c2' : '#e0f2fe', color: filter === tab ? '#fff' : '#1880c2'
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>
            <input 
              placeholder="Search by ID or Medicine..." 
              value={q} 
              onChange={e => setQ(e.target.value)} 
              style={{ ...styles.input, width: isMobile ? '100%' : 250 }} 
            />
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Report ID & Date</th>
                  <th style={styles.th}>Medicine Info</th>
                  <th style={styles.th}>Damage Type</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Loss Value</th>
                  <th style={styles.th}>Status</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDamages.map(d => (
                  <tr key={d._id} style={{ background: d.status === 'Pending' ? '#fefce8' : 'transparent' }}>
                    <td style={styles.td}>
                      <strong>{d.damageInvoiceNumber}</strong><br/>
                      <span style={{ fontSize: 12, color: '#64748b' }}>{new Date(d.dateReported).toLocaleDateString()}</span>
                    </td>
                    <td style={styles.td}>
                      <strong>{d.medicine?.name}</strong><br/>
                      <span style={{ fontSize: 12, color: '#64748b' }}>Qty: {d.quantity} {d.medicine?.quantityUnit}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ background: '#f1f5f9', padding: '4px 8px', borderRadius: 4, fontSize: 12 }}>{d.damageType}</span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>
                      Rs {fmt(d.totalLossValue)}
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        background: d.status === 'Pending' ? '#fef3c7' : d.status === 'Approved' ? '#dcfce7' : '#fee2e2',
                        color: d.status === 'Pending' ? '#b45309' : d.status === 'Approved' ? '#166534' : '#991b1b'
                      }}>
                        {d.status}
                      </span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      {d.status === 'Pending' ? (
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                          <button onClick={() => setApproveModal(d)} style={{ padding: '6px 12px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>Approve</button>
                          <button onClick={() => setRejectModal(d)} style={{ padding: '6px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>Reject</button>
                        </div>
                      ) : d.reportHtml ? (
                        <button onClick={() => handleViewReport(d._id)} style={{ padding: '6px 12px', background: '#1880c2', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>📄 View Audit</button>
                      ) : (
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>Processed</span>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredDamages.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No damage records found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* REPORT DAMAGE MODAL */}
      {showAddForm && (
        <div style={styles.modal} onClick={() => setShowAddForm(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={{ padding: 20, background: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
              <h2 style={{ margin: 0, color: '#991b1b', fontSize: 18 }}>Report Damage/Loss</h2>
              <p style={{ margin: '5px 0 0', fontSize: 12, color: '#dc2626' }}>Stock will be deducted immediately. Requires Admin approval.</p>
            </div>
            <form onSubmit={handleReportSubmit} style={{ padding: 24 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#4a5568', marginBottom: 6, display: 'block' }}>Medicine *</label>
                  <select 
                    value={formData.medicineId} 
                    onChange={e => setFormData({...formData, medicineId: e.target.value})} 
                    style={styles.input} required
                  >
                    <option value="">Select Medicine</option>
                    {medicines.map(m => <option key={m._id} value={m._id}>{m.name} (Stock: {m.quantity})</option>)}
                  </select>
                </div>
                
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#4a5568', marginBottom: 6, display: 'block' }}>Batch *</label>
                  <select 
                    value={formData.batchId} 
                    onChange={e => setFormData({...formData, batchId: e.target.value})} 
                    style={styles.input} required disabled={!formData.medicineId}
                  >
                    <option value="">{batches.length ? 'Select Batch' : 'Select Medicine First'}</option>
                    {batches.map(b => <option key={b._id} value={b._id}>{b.batchNumber} (Qty: {b.quantity})</option>)}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#4a5568', marginBottom: 6, display: 'block' }}>Damage Type *</label>
                    <select value={formData.damageType} onChange={e => setFormData({...formData, damageType: e.target.value})} style={styles.input} required>
                      <option value="Broken/Damaged">Broken/Physical Damage</option>
                      <option value="Expired">Expired (Non-Returnable)</option>
                      <option value="Contaminated">Contaminated</option>
                      <option value="Theft/Missing">Theft/Missing</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#4a5568', marginBottom: 6, display: 'block' }}>Quantity Lost *</label>
                    <input type="number" min="1" value={formData.quantity} onChange={e => setFormData({...formData, quantity: e.target.value})} style={styles.input} required />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#4a5568', marginBottom: 6, display: 'block' }}>Notes/Reason *</label>
                  <textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} style={{...styles.input, resize: 'none', height: 80}} placeholder="Provide clear reason for damage..." required />
                </div>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
                <button type="button" onClick={() => setShowAddForm(false)} style={styles.btnSecondary}>Cancel</button>
                <button type="submit" style={styles.btnDanger}>Report & Deduct Stock</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* APPROVE MODAL */}
      {approveModal && (
        <div style={styles.modal} onClick={() => setApproveModal(null)}>
          <div style={{...styles.modalContent, padding: 24}} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 10px', color: '#16a34a' }}>Approve Write-off</h2>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
              This will officially record a financial loss of <strong>Rs {fmt(approveModal.totalLossValue)}</strong> in the accounting ledger. An official audit document will be generated.
            </p>
            <div style={{ background: '#f8fafc', padding: 15, borderRadius: 8, marginBottom: 20, fontSize: 13 }}>
              <strong>Item:</strong> {approveModal.medicine?.name}<br/>
              <strong>Type:</strong> {approveModal.damageType}<br/>
              <strong>Qty:</strong> {approveModal.quantity}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setApproveModal(null)} style={styles.btnSecondary}>Cancel</button>
              <button onClick={() => generateAuditReport(approveModal)} style={{...styles.btnPrimary, background: '#16a34a'}}>Confirm & Generate Audit</button>
            </div>
          </div>
        </div>
      )}

      {/* REJECT MODAL */}
      {rejectModal && (
        <div style={styles.modal} onClick={() => setRejectModal(null)}>
          <div style={{...styles.modalContent, padding: 24}} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 10px', color: '#dc2626' }}>Reject Damage Request</h2>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
              This will return <strong>{rejectModal.quantity} units</strong> of {rejectModal.medicine?.name} back to active inventory.
            </p>
            <form onSubmit={handleReject}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#4a5568', marginBottom: 6, display: 'block' }}>Rejection Reason *</label>
              <input type="text" required value={rejectReason} onChange={e => setRejectReason(e.target.value)} style={styles.input} placeholder="e.g., Found the missing stock" autoFocus />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" onClick={() => setRejectModal(null)} style={styles.btnSecondary}>Cancel</button>
                <button type="submit" style={styles.btnDanger}>Reject & Revert Stock</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}